// @i18n-ignore-file
// eslint-disable-next-line eslint-comments/disable-enable-pair
// https://lastbrand.atlassian.net/browse/ECOMP-4497
import { getAuthToken } from '@lastbrand/fe-common/universal-auth'
import HttpClient, {
  AxiosBasicCredentials,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosError as HttpClientError,
  AxiosInstance as HttpClientInstance,
  AxiosResponse as HttpClientResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios'
import axiosRetry from 'axios-retry'
import { isBrowser } from 'react-device-detect'

import {
  API_ADDRESS_SERVICE_ORIGNIN,
  API_BFF_ORIGIN,
  API_OLD_ADDRESS_SERVICE_ORIGNIN,
  API_SF_RUNTIME_BASE_URL,
  API_SGTM_BASE_URL,
  API_STATE_META_ADDRESS_SERVICE_ORIGNIN,
  CONFIG_ADDRESS_VALIDATE_API,
  CONFIG_CART_SERVICES,
  CONFIG_INVENTORY_SERVICES,
  CONFIG_PRODUCT_CATALOG_ENDPOINT,
  CONFIG_PRODUCT_REVIEW_ENDPOINT,
  CONFIG_QUINCE_RETURN_SERVICE_API,
  CONFIG_QUINCE_SERIVCES_API,
  CONFIG_QUINCE_STORE_FRONT_BUILD_API,
  CONFIG_REPORTING_SERVICES,
  CONFIG_UPG_ENDPOINT,
  IS_PROD_BUILD,
  API_SF_BASE_URL,
  CONFIG_SAND_SERVICES,
  API_PRESENTATION_LAYER_SERVICE,
  CONFIG_CHECKOUT_SERVICES,
  API_SF_SAND_PRODUCT_BASE_URL,
  CONFIG_ZIPCODE_SERVICE_API,
  API_STORE_PROGRAM_SERVICE,
  CONFIG_QUINCE_ORDER_AGGREGATOR_SERVICES_API,
} from '@src/config/env'
import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { LB_USER_ID_HEADER } from '@src/constants/api.headers.constants'
import { IExperimentLoggingData } from '@src/experiments/experiments.types'
import { statsigExperimentManager } from '@src/experiments/statsig-integration/statsig-experiment.manager'
import { FEATURE_HEADERS } from '@src/quince-site-common/src/utils/constants'
import { getB2BHeaders, getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'
import {
  getHeadersWithContentVersion,
  getContentVersionOnClient,
  getLocalisationHeaders,
} from '@src/utils/general.utils'

import { getCheckoutAuthCodeFromHeader } from '../services/cartCheckoutService/checkout.service.helper'
import { getCartAuthCodeFromHeader } from '../services/cartService/cart.service.helper'

import AuthUtil from './authUtil'
import { getContentProductsEnv } from './getEnvironmentParam'
import getFormattedUrlForEnv from './getFormattedUrlForEnv/getFormattedUrlForEnv'
import { getlbUserId } from './getlb-user-id'

const authInterceptor = async (config: InternalAxiosRequestConfig) => {
  const token = await getAuthToken()
  if (token) {
    config.headers.Authorization = token
  }
  return config
}

class BaseHttpClient {
  private readonly client: HttpClientInstance

  constructor(endPoint: string, basicAuth?: AxiosBasicCredentials, skipAuth = false) {
    this.client = HttpClient.create({
      baseURL: endPoint,
    })
    this.client.interceptors.request.use(request => {
      request.auth = basicAuth

      // Set the CONTENT_VERSION_HEADER
      const contentVersion = getContentVersionOnClient()
      if (contentVersion) {
        request.headers = getHeadersWithContentVersion(request.headers) as AxiosRequestHeaders
      }
      const localisationHeaders = getLocalisationHeaders(request.baseURL)
      const b2bHeaders = getB2BHeaders(request.baseURL)
      request.headers = {
        ...request.headers,
        ...b2bHeaders,
        ...localisationHeaders,
        ...b2bHeaders,
      } as AxiosRequestHeaders
      return request
    })

    this.client.interceptors.response.use(null, async function (error) {
      const isUnauthRes = error.response?.status === 401 || error.response?.status === 403
      if (IS_PROD_BUILD && isUnauthRes && isBrowser && !skipAuth) {
        await window.logoutUser?.(false, true)
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    })
  }

  addRequestInterceptor(
    requestInterceptor: (
      value: InternalAxiosRequestConfig,
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
  ) {
    this.client.interceptors.request.use(requestInterceptor)
  }

  addRetry({ retryCount, delay } = { retryCount: 1, delay: 1000 }) {
    axiosRetry(this.client, { retries: retryCount, retryDelay: () => delay })
  }

  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config)
  }

  post<T = any, D = any>(url: string, data: T, config?: AxiosRequestConfig) {
    return this.client.post<T, AxiosResponse<D>>(url, data, config)
  }

  put<T = any, D = any>(url: string, data: T, config?: AxiosRequestConfig) {
    return this.client.put<T, AxiosResponse<D>>(url, data, config)
  }

  delete<T = any, D = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T, AxiosResponse<D>>(url, config)
  }
}

class StorefrontRuntimeHttpClient extends BaseHttpClient {
  // Passing new website flag always for storefront apis.
  override get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const updatedConfig = config ?? {}
    const params = updatedConfig.params ?? {}
    updatedConfig.params = {
      ...params,
      isNewWebsite: 'true',
    }
    return super.get(url, updatedConfig)
  }
}

class StorefrontBuildTimeHttpClient extends BaseHttpClient {
  // Passing new website flag always for storefront apis.
  override get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const updatedConfig = config ?? {}
    const params = updatedConfig.params ?? {}
    updatedConfig.params = {
      ...params,
      isNewWebsite: 'true',
    }
    return super.get(url, updatedConfig)
  }
}

const productCatalogHttpClient = new BaseHttpClient(CONFIG_PRODUCT_CATALOG_ENDPOINT)

const productReviewHttpClient = new BaseHttpClient(CONFIG_PRODUCT_REVIEW_ENDPOINT)

const storefrontBuildHttpClient = new StorefrontBuildTimeHttpClient(CONFIG_QUINCE_STORE_FRONT_BUILD_API)

const storefrontSandProductServiceHttpClient = new BaseHttpClient(API_SF_SAND_PRODUCT_BASE_URL)
const storefrontSandProductServiceAuthHttpClient = new BaseHttpClient(API_SF_SAND_PRODUCT_BASE_URL)

storefrontSandProductServiceAuthHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `${userSession.idToken.jwtToken}`
  }
  return request
})

const reportingServicesHttpClient = new BaseHttpClient(CONFIG_REPORTING_SERVICES)
const reportingServicesStagingHttpClient = new BaseHttpClient('https://api-staging.onequince.com')

const reportingServicesHttpClientWithoutRetry = new BaseHttpClient(CONFIG_REPORTING_SERVICES)
const reportingTradeServicesHttpClient = new BaseHttpClient(CONFIG_REPORTING_SERVICES)

const sandServicesHttpClient = new BaseHttpClient(CONFIG_SAND_SERVICES)

sandServicesHttpClient.addRequestInterceptor(async request => {
  const storeProgramId = getStoreProgramIdFromStorage()
  if (storeProgramId) {
    const userSession = await AuthUtil.getUserSession()
    if (userSession?.idToken?.jwtToken) {
      request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
    }
  }
  return request
})

reportingServicesHttpClient.addRetry({
  retryCount: 1,
  delay: 60 * 1000, // 60 seconds
})

reportingTradeServicesHttpClient.addRetry({
  retryCount: 1,
  delay: 60 * 1000, // 60 seconds
})

const storefrontRuntimeHttpClient = new StorefrontRuntimeHttpClient(API_SF_RUNTIME_BASE_URL, undefined)
storefrontRuntimeHttpClient.addRetry({
  retryCount: 2,
  delay: 500, // .5s
})

storefrontRuntimeHttpClient.addRequestInterceptor(request => {
  request.url = getFormattedUrlForEnv(request.url)
  return request
})

const quinceServicesHttpClient = new BaseHttpClient(CONFIG_QUINCE_SERIVCES_API, undefined)
const quinceServicesPublicHttpClient = new BaseHttpClient(CONFIG_QUINCE_SERIVCES_API, undefined)
const quinceOrderAggregatorServiceHttpClient = new BaseHttpClient(
  CONFIG_QUINCE_ORDER_AGGREGATOR_SERVICES_API,
  undefined,
)
const quinceTrustAndSafetyServiceHttpClient = new BaseHttpClient(API_ADDRESS_SERVICE_ORIGNIN, undefined, true)
const quinceReturnServiceHttpClient = new BaseHttpClient(CONFIG_QUINCE_RETURN_SERVICE_API, undefined)

const storefrontZipcodeServicesHttpClient = new BaseHttpClient(CONFIG_ZIPCODE_SERVICE_API)

storefrontZipcodeServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = userSession?.accessToken?.jwtToken
  return request
})

const storefrontCartServicesHttpClient = new BaseHttpClient(CONFIG_CART_SERVICES)

storefrontCartServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()

  // send out of stock cart experiment bucket
  const experimentBuckets = statsigExperimentManager.getExposedBuckets()

  let outOfStockCartExperimentBucket = experimentBuckets.find(
    exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART,
  )?.bucketName

  // if the experiment bucket is not found, check local storage as for force bucketing logics on the initial load
  // this is to ensure that the experiment bucket is always set on the initial load if statsig is not ready
  if (!outOfStockCartExperimentBucket && isBrowser) {
    const existingBuckets = JSON.parse(
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem('statsigExperimentBuckets') ?? '[]',
    ) as IExperimentLoggingData[]
    // eslint-disable-next-line @quince/no-direct-localstorage
    const forceBuckets = JSON.parse(localStorage.getItem('statsigForcedBuckets') ?? '{}')
    outOfStockCartExperimentBucket =
      existingBuckets.find(exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART)?.bucketName ??
      forceBuckets[STATSIG_EXPERIMENTS.OOS_CART]
  }

  if (outOfStockCartExperimentBucket) {
    request.headers['X-Buckets'] = JSON.stringify({
      [STATSIG_EXPERIMENTS.OOS_CART]: outOfStockCartExperimentBucket,
    })
  }

  const authCode = getCartAuthCodeFromHeader()
  if (authCode) {
    request.headers['auth-code'] = authCode
  }
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const storefrontCheckoutServicesHttpClient = new BaseHttpClient(CONFIG_CHECKOUT_SERVICES)

storefrontCheckoutServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()

  // send out of stock cart experiment bucket
  const experimentBuckets = statsigExperimentManager.getExposedBuckets()

  let outOfStockCartExperimentBucket = experimentBuckets.find(
    exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART,
  )?.bucketName

  // if the experiment bucket is not found, check local storage as for force bucketing logics on the initial load
  // this is to ensure that the experiment bucket is always set on the initial load if statsig is not ready
  if (!outOfStockCartExperimentBucket && isBrowser) {
    const existingBuckets = JSON.parse(
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem('statsigExperimentBuckets') ?? '[]',
    ) as IExperimentLoggingData[]
    // eslint-disable-next-line @quince/no-direct-localstorage
    const forceBuckets = JSON.parse(localStorage.getItem('statsigForcedBuckets') ?? '{}')
    outOfStockCartExperimentBucket =
      existingBuckets.find(exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART)?.bucketName ??
      forceBuckets[STATSIG_EXPERIMENTS.OOS_CART]
  }

  const exposedExperiments = statsigExperimentManager.getExposedBuckets()
  const eddExpBucket = exposedExperiments.find(experiment => experiment.experimentName === STATSIG_EXPERIMENTS.EDD)

  let expHeaders = {}
  if (eddExpBucket) {
    expHeaders = {
      ...expHeaders,
      [STATSIG_EXPERIMENTS.EDD]: eddExpBucket.bucketName,
    }
  }

  if (outOfStockCartExperimentBucket) {
    expHeaders = {
      ...expHeaders,
      [STATSIG_EXPERIMENTS.OOS_CART]: outOfStockCartExperimentBucket,
    }
  }

  if (Object.keys(expHeaders).length > 0) {
    request.headers['X-Buckets'] = JSON.stringify(expHeaders)
  }

  const { checkoutAuthCode = '', cartAuthCode = '' } = getCheckoutAuthCodeFromHeader()
  if (checkoutAuthCode) {
    request.headers['auth-code'] = checkoutAuthCode
  }
  if (cartAuthCode) {
    request.headers['cart-auth-code'] = cartAuthCode
  }
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const storefrontBaseServicesHttpClient = new BaseHttpClient(API_SF_BASE_URL)

storefrontBaseServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const storefrontInventoryServicesHttpClient = new BaseHttpClient(CONFIG_INVENTORY_SERVICES)

quinceServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})
quinceOrderAggregatorServiceHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})
quinceTrustAndSafetyServiceHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})

quinceServicesPublicHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const unifiedPaymentGatewayClient = new BaseHttpClient(CONFIG_UPG_ENDPOINT)

unifiedPaymentGatewayClient.addRequestInterceptor(authInterceptor)

quinceReturnServiceHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})

reportingServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})

export const storefrontRegionsHttpClient = new BaseHttpClient(`${CONFIG_REPORTING_SERVICES}/region-management-service`)

//const stampedHttpClient = new BaseHttpClient('https://stamped.io/api/', undefined);

const customerServiceClient = new BaseHttpClient(`${API_OLD_ADDRESS_SERVICE_ORIGNIN}/customers`)
const addressValidationClient = new BaseHttpClient(`${CONFIG_ADDRESS_VALIDATE_API}/address/v1`)

customerServiceClient.addRequestInterceptor(authInterceptor)

const addressServiceClient = new BaseHttpClient(API_ADDRESS_SERVICE_ORIGNIN)
addressServiceClient.addRequestInterceptor(authInterceptor)

const addressStateMetaServiceClient = new BaseHttpClient(API_STATE_META_ADDRESS_SERVICE_ORIGNIN)
addressStateMetaServiceClient.addRequestInterceptor(authInterceptor)

const sgtmServiceClient = new BaseHttpClient(API_SGTM_BASE_URL)

// this is used only in client side, s2s uses next fetch api
const bffClient = new BaseHttpClient(API_BFF_ORIGIN)
bffClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  // Set the LB_USER_ID_HEADER
  const lbUserId = getlbUserId()
  if (lbUserId) {
    request.headers[LB_USER_ID_HEADER] = lbUserId
  }

  if (userSession) {
    request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  }

  request.headers[FEATURE_HEADERS.CONTENT_ENV] = getContentProductsEnv()

  return request
})

const presentationServiceClient = new BaseHttpClient(API_PRESENTATION_LAYER_SERVICE)
const storeProgramServiceClient = new BaseHttpClient(API_STORE_PROGRAM_SERVICE)

const isHttpClientError = isAxiosError
export {
  addressServiceClient,
  addressValidationClient,
  bffClient,
  customerServiceClient,
  isHttpClientError,
  productCatalogHttpClient,
  productReviewHttpClient,
  quinceReturnServiceHttpClient,
  quinceServicesHttpClient,
  quinceOrderAggregatorServiceHttpClient,
  quinceTrustAndSafetyServiceHttpClient,
  quinceServicesPublicHttpClient,
  reportingServicesHttpClient,
  reportingServicesHttpClientWithoutRetry,
  reportingServicesStagingHttpClient,
  reportingTradeServicesHttpClient,
  storefrontBuildHttpClient,
  storefrontCartServicesHttpClient,
  storefrontInventoryServicesHttpClient,
  storefrontRuntimeHttpClient,
  storefrontZipcodeServicesHttpClient,
  sgtmServiceClient,
  addressStateMetaServiceClient,
  unifiedPaymentGatewayClient,
  storefrontBaseServicesHttpClient,
  sandServicesHttpClient,
  presentationServiceClient,
  storefrontCheckoutServicesHttpClient,
  storefrontSandProductServiceHttpClient,
  storefrontSandProductServiceAuthHttpClient,
  storeProgramServiceClient,
}

export type { HttpClientResponse, HttpClientError }
